import React, { useMemo, useState } from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";
import { Section, ErrorNote, Spinner, ConfirmDialog } from "../components/ui.jsx";
import { primaryBtnStyle, secondaryBtnStyle, ghostBtnStyle, btnState } from "../components/styles.js";
import { IdentityFields, PurchaseFields, DnaFields } from "../components/ItemFields.jsx";
import { categoryMeta } from "../domain/categories.js";
import { generatePreservationProfile } from "../domain/preservation.js";
import { formFromItem, payloadFromForm, isFormComplete } from "../domain/itemForm.js";

/* One page rather than the Add wizard's five steps: editing is usually
   correcting a single field, and making someone walk a wizard to fix a typo
   would be worse than not having edit at all.

   Category is intentionally not editable here — changing it would invalidate
   every material selection, which is a delete-and-re-add, not an edit. */
export default function EditItem({ item, onSave, onDelete, onCancel }) {
  const [form, setForm] = useState(() => formFromItem(item));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const meta = categoryMeta(form.category);

  const profile = useMemo(
    () => generatePreservationProfile({ category: form.category, status: form.status, materials: form.materials }),
    [form.category, form.status, form.materials]
  );

  const complete = isFormComplete(form);
  const busy = saving || deleting;

  async function handleSave() {
    if (busy || !complete) return;
    setSaving(true);
    setError(null);
    const { error: err } = await onSave(item.id, payloadFromForm(form));
    if (err) {
      setError(err);
      setSaving(false);
    }
    // On success the parent closes this screen.
  }

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    const { error: err } = await onDelete(item.id);
    if (err) {
      setError(err);
      setDeleting(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {confirmingDelete && (
        <ConfirmDialog
          title={`Delete ${item.brand} ${item.model}?`}
          body={`This removes the item and its entire history — ${item.wears} logged ${meta.wearVerbPlural.toLowerCase()} and every clean and rotation. It cannot be undone.`}
          confirmLabel="Delete"
          busy={deleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}

      <button onClick={onCancel} style={ghostBtnStyle}>
        ← Cancel
      </button>

      <div>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 21, color: COLORS.cream }}>Edit item</div>
        <div style={{ fontSize: 12, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 3 }}>
          {meta.icon} {meta.label} · wear history and condition are left untouched
        </div>
      </div>

      <ErrorNote message={error} />

      <Section title="Identity">
        <IdentityFields form={form} set={set} />
      </Section>

      <Section title="Purchase information">
        <PurchaseFields form={form} set={set} />
      </Section>

      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 17, color: COLORS.cream }}>{meta.dnaTitle}</div>
      <DnaFields form={form} setForm={setForm} />

      <Section title="Updated preservation profile">
        <div style={{ background: COLORS.blackCard, border: `1px solid ${COLORS.line}`, borderRadius: 14, padding: 16 }}>
          {profile.map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "8px 0", borderTop: `1px solid ${COLORS.lineFaint}`, fontSize: 12.5, fontFamily: FONT_BODY }}>
              <span style={{ color: COLORS.creamDim, flex: "none" }}>{k}</span>
              <span style={{ color: COLORS.cream, textAlign: "right" }}>{v}</span>
            </div>
          ))}
        </div>
      </Section>

      {!complete && (
        <div style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>
          Brand and model are required.
        </div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onCancel} disabled={busy} style={btnState(secondaryBtnStyle, !busy)}>
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={busy || !complete}
          style={{ ...btnState(primaryBtnStyle, !busy && complete), display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
        >
          {saving && <Spinner size={14} color="#1A1408" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div style={{ borderTop: `1px solid ${COLORS.lineFaint}`, paddingTop: 18, marginTop: 4 }}>
        <div style={{ fontSize: 10.5, fontFamily: FONT_MONO, letterSpacing: 1.2, textTransform: "uppercase", color: COLORS.red, marginBottom: 8 }}>
          Danger zone
        </div>
        <div style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginBottom: 10, lineHeight: 1.5 }}>
          Removing an item also removes its logged history. Sold something? Deleting it loses the record of it.
        </div>
        <button
          onClick={() => setConfirmingDelete(true)}
          disabled={busy}
          style={{
            width: "100%", background: "transparent", color: COLORS.red, border: `1px solid ${COLORS.red}`,
            borderRadius: 9, padding: "12px 16px", fontSize: 14, fontWeight: 600, fontFamily: FONT_BODY,
            cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.45 : 1,
          }}
        >
          Delete this item
        </button>
      </div>
    </div>
  );
}
